/**
 * React Query hooks for stock class birthing periods.
 * React Query hooks for the corresponding entity.
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";

export interface StockClassBirthingPeriod {
  guid: string;
  dateStart: string;
  dateEnd: string;
  expectedNumAnimalsWithoutPregnancy: number;
  expectedNumOffspringPerAnimal: number;
  stockClassGuid: string;
}

export interface StockClassWithBirthingPeriods {
  guid: string;
  name: string;
  animalGroup: string;
  animalType?: string;
  birthingPeriodValues: StockClassBirthingPeriod[];
  [key: string]: unknown;
}

const stockClassBirthingPeriodApi = new API<StockClassBirthingPeriod>(
  "stockClassBirthingPeriods"
);

/**
 * Fetch birthing periods by their IDs
 */
export function useBirthingPeriodsByIds(
  birthingPeriodIds: string[] | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: ["birthingPeriods", "byIds", birthingPeriodIds?.sort().join(",")],
    queryFn: async () => {
      if (!birthingPeriodIds || birthingPeriodIds.length === 0) {
        return [];
      }
      return await stockClassBirthingPeriodApi.getMany(birthingPeriodIds);
    },
    enabled: enabled && !!birthingPeriodIds && birthingPeriodIds.length > 0,
  });
}

/**
 * Create a birthing period
 */
export function useCreateBirthingPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (birthingPeriod: StockClassBirthingPeriod) => {
      const result = await stockClassBirthingPeriodApi.createOne(
        birthingPeriod
      );
      return await stockClassBirthingPeriodApi.getOne(result.guid);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.birthingPeriods.byStockClass(data.stockClassGuid),
        (old: StockClassBirthingPeriod[] | undefined) => [...(old || []), data]
      );
    },
  });
}

/**
 * Update a birthing period
 */
export function useUpdateBirthingPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (birthingPeriod: StockClassBirthingPeriod) => {
      await stockClassBirthingPeriodApi.updateOne(birthingPeriod);
      return await stockClassBirthingPeriodApi.getOne(birthingPeriod.guid);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.birthingPeriods.byStockClass(data.stockClassGuid),
        (old: StockClassBirthingPeriod[] | undefined) =>
          (old || []).map((bp) => (bp.guid === data.guid ? data : bp))
      );
    },
  });
}

/**
 * Delete a birthing period
 */
export function useDeleteBirthingPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      birthingPeriodId: string;
      stockClassGuid: string;
    }) => {
      await stockClassBirthingPeriodApi.deleteOne(params.birthingPeriodId);
      return params;
    },
    onSuccess: ({ birthingPeriodId, stockClassGuid }) => {
      queryClient.setQueryData(
        queryKeys.birthingPeriods.byStockClass(stockClassGuid),
        (old: StockClassBirthingPeriod[] | undefined) =>
          (old || []).filter((bp) => bp.guid !== birthingPeriodId)
      );
    },
  });
}

/**
 * Prefetch birthing periods by their IDs (used during initialization)
 */
export async function fetchBirthingPeriodsByIdsQuery(
  birthingPeriodIds: string[],
  queryClient: QueryClient
): Promise<StockClassBirthingPeriod[]> {
  if (birthingPeriodIds.length === 0) return [];

  const periods = await stockClassBirthingPeriodApi.getMany(birthingPeriodIds);

  // Group by stock class and cache
  const byStockClass: Record<string, StockClassBirthingPeriod[]> = {};
  periods.forEach((bp) => {
    if (!byStockClass[bp.stockClassGuid]) {
      byStockClass[bp.stockClassGuid] = [];
    }
    byStockClass[bp.stockClassGuid].push(bp);
  });

  Object.entries(byStockClass).forEach(([stockClassGuid, periods]) => {
    queryClient.setQueryData(
      queryKeys.birthingPeriods.byStockClass(stockClassGuid),
      periods
    );
  });

  return periods;
}

/**
 * Reset birthing periods query cache
 */
export function resetBirthingPeriodsQueryCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: queryKeys.birthingPeriods.all });
}
